import React, { useState, useEffect } from "react";
import { Container, Div, Text } from "../../styles/Common";
import { useNavigate, useParams } from "react-router-dom";
import { theme } from "../../styles/theme";
import back from "../../assets/icons/arrow-left.svg";
import { Link } from "../../components/Link";
import { validateNewProject } from "../../utilities/validations";
import { Alert } from "antd";
import { Button } from "../../components/Button";
import useCompanies from "../Projects/hooks/useCompanies";
import useUpdateProject from "../Projects/hooks/useUpdateProject";
import useGetProjectInfo from "../Projects/hooks/useGetProjectInfo";
import { ProjectForm } from "../CreateProject/components/ProjectForm";
import { UsersList } from "../CreateProject/components/UsersList";
import { hasPermission, setPermissions } from "../../utilities/permissions";
import toast from "react-hot-toast";
import { setLocalUser } from "../../core/services/axiosInstance";
import useGetPermissions from "../../core/hooks/useGetPermissions";

export const UpdateProject = () => {
  const navigate = useNavigate();
  const getPermissions = useGetPermissions();
  const { projectId } = useParams();
  const update = useUpdateProject();
  const { isLoading } = update;
  const getProjectInfo = useGetProjectInfo(projectId);
  const {
    data: projectInfo,
    isSuccess,
    isLoading: isLoadingUpdate,
    refetch
  } = getProjectInfo;
  const { data: companies } = useCompanies();
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);

  const editProject = hasPermission(projectId, "Edit Project");

  const initialValues = {
    companyId: null,
    ownerCompany: "",
    projectAFE: "",
    projectName: "",
    projectManager: "",
    projectManagerPhoneCodeId: null,
    projectManagerPhone: "",
    projectManagerEmail: "",
    stateId: null,
    address: "",
    addressZipCode: "",
    coordinates: "",
    coordinatesZipCode: "",
  };

  const initialValuesError = {
    companyId: {
      error: false,
      message: "",
    },
    ownerCompany: {
      error: false,
      message: "",
    },
    projectAFE: {
      error: false,
      message: "",
    },
    projectName: {
      error: false,
      message: "",
    },
    projectManager: {
      error: false,
      message: "",
    },
    stateId: {
      error: false,
      message: "",
    },
    address: {
      error: false,
      message: "",
    },
    addressZipCode: {
      error: false,
      message: "",
    },
    coordinates: {
      error: false,
      message: "",
    },
    coordinatesZipCode: {
      error: false,
      message: "",
    },
    projectManagerPhoneCodeId: {
      error: false,
      message: "",
    },
    projectManagerPhone: {
      error: false,
      message: "",
    },
    projectManagerEmail: {
      error: false,
      message: "",
    },
  };

  const [projectForm, setProjectForm] = useState(initialValues);
  const [projectErrorForm, setProjectErrorForm] =
    useState(initialValuesError);
  const [userRolesProject, setUserRolesProject] = useState([]);
  const [initialUserRolesProject, setInitialUserRolesProject] = useState([]);
  const [hasNewUsers, setHasNewUsers] = useState(false);
  const [usersInfo, setUsersInfo] = useState([
    {
      id: 1,
      userId: "",
      role: "",
    }
  ]);

  useEffect(() => {
    if (companies?.companies.length === 1) {
      setProjectForm({
        ...projectForm,
        companyId: companies.companies[0].id,
      });
    }
  }, [companies]);

  useEffect(() => {
    if (companies?.companies.length > 0) {
      setCompaniesOptions(
        companies?.companies.map((ele) => ({
          value: ele.id,
          label: ele.companyName,
        }))
      );
    }
  }, [companies]);

  useEffect(() => {
    if (isSuccess) {
      setProjectForm({
        companyId: projectInfo?.data.companyId,
        ownerCompany: projectInfo?.data.ownerCompany,
        projectAFE: projectInfo?.data.projectAFE,
        projectName: projectInfo?.data.projectName,
        projectManager: projectInfo?.data.projectManager,
        projectManagerPhoneCodeId: projectInfo?.data.projectManagerPhoneCodeId,
        projectManagerPhone: projectInfo?.data.projectManagerPhone,
        projectManagerEmail: projectInfo?.data.projectManagerEmail,
        stateId: projectInfo?.data.stateId,
        address: projectInfo?.data.address,
        addressZipCode: projectInfo?.data.addressZipCode,
        coordinates: projectInfo?.data.coordinates,
        coordinatesZipCode: projectInfo?.data.coordinatesZipCode,
      });
      setUserRolesProject(projectInfo?.data.userRolesProject || []);
      setInitialUserRolesProject(projectInfo?.data.userRolesProject || []);
    }
  }, [isSuccess, projectInfo]);

  useEffect(() => {
    if (userRolesProject.length > initialUserRolesProject.length) {
      setHasNewUsers(true);
    } else {
      setHasNewUsers(false);
    }
  }, [userRolesProject, initialUserRolesProject]);

  const handleBack = () => {
    navigate("/projects");
  };

  const handleCancel = () => {
    navigate("/projects");
    setProjectForm(initialValues);
    setProjectErrorForm(initialValuesError);
  };

  const companyName = (id) => {
    const company = companiesOptions.find((ele) => ele.value === id)?.label;

    return company;
  };

  const handleUpdateProject = () => {
    const validation = validateNewProject.validate(projectForm, {
      abortEarly: false,
    });

    if (validation.error) {
      const newErrorForm = projectErrorForm;
      validation.error.details.forEach((ele) => {
        newErrorForm[ele.context.label].error = true;
        newErrorForm[ele.context.label].message =
          "Required field or invalid format";
        setProjectErrorForm(newErrorForm);
        setForceUpdate(!forceUpdate);
      });
    } else {
      update.reset();
      update.mutate(
        {
          ...projectForm,
          projectId: projectId,
          ownerCompany:
            projectForm.ownerCompany === ""
              ? companyName(projectForm.companyId)
              : projectForm.ownerCompany,
          userRolesProject: userRolesProject,
        },
        {
          onSuccess: (response) => {
            setLocalUser(response?.data?.Result);
            getPermissions.reset();
            getPermissions.mutate(null, {
              onSuccess: (response) => {
                setPermissions(response?.data);
                toast.success("This project has been updated");
                refetch();
              },
            });
          },
          onError: (err) => {
            setErrorMessage(err.response.data.Errors[0]);
          },
        }
      );
    }
  };

  return (
    <Container>
      <Div m="0 0 15px 0">
        <Link
          onClick={handleBack}
          color={theme.colors.grey1}
          icon={<img alt="back" src={back} style={{ marginRight: "12px" }} />}
        >
          Back
        </Link>
      </Div>
      <Div direction="column" gap="8px" m="0 0 32px 0">
        <Text
          size={theme.fonts.size.h4}
          color={theme.colors.dark}
          weight={theme.fonts.weight.bold}
        >
          Update Project
        </Text>
        <Text
          size={theme.fonts.size.sm}
          color={theme.colors.grey1}
        >
          Enter the project details to start organizing documentation and assigning user suppliers. You can edit this information at any time.
        </Text>
      </Div>
      <Div width="100%" justify="space-between" m="0 0 48px 0">
        <ProjectForm 
          projectForm={projectForm}
          setProjectForm={setProjectForm}
          projectErrorForm={projectErrorForm}
          setProjectErrorForm={setProjectErrorForm}
          companiesOptions={companiesOptions}
          isLoading={isLoadingUpdate}
          editProject={editProject}
        />
        <UsersList
          userRolesProject={userRolesProject}
          setUserRolesProject={setUserRolesProject}
          projectId={projectId}
          usersInfo={usersInfo}
          setUsersInfo={setUsersInfo}
          editProject={editProject}
          hasNewUsers={hasNewUsers}
          initialUserRolesProject={initialUserRolesProject}
        />
      </Div>
      <Div direction="column" width="100%" justify="flex-start">
        {errorMessage !== "" && (
          <Div m="0 0 20px 0" justify="flex-start">
            <Alert closable message={`${errorMessage}`} type="error" />
          </Div>
        )}
        <Div gap="32px" justify="flex-start">
          <Button
            onClick={handleCancel}
            border={theme.colors.dark}
            color={theme.colors.dark}
          >
            Cancel
          </Button>
          {editProject && (
            <Button
              onClick={handleUpdateProject}
              background={theme.colors.salmon}
              loading={isLoading}
            >
              Update Project
            </Button>
          )}
        </Div>
      </Div>
    </Container>
  )
};
